import type {
  ApplicationStructure,
  ApplicationTemplateId,
} from '../types/audition.types';
import { getCompetitionApplicationStructure } from './competition-application-structure';

/**
 * Default application form structure for general auditions.
 * Mirrors the defaultApplicationStructure from the Meteor reference.
 *
 * Profile-bound fields carry `source: 'profile'` so the form builder badges
 * them and the apply flow prefills them from the dancer's profile +
 * backfills (fill-if-null) on submit via PROFILE_FIELD_MAP. They are NOT
 * `locked` here — audition organizers stay free to remove or re-order them
 * (locking is a competition-booklet concept; see COMPETITION_BOOKLET_FIELDS).
 */
export const DEFAULT_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    {
      key: 'personalSection',
      type: 'heading',
      label: 'Personal Information',
      sortOrder: 0,
    },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      placeholder: 'Your first name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 1,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      placeholder: 'Your last name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Contact Email',
      placeholder: 'your@email.com',
      source: 'profile',
      validation: { required: true },
      width: 'full',
      sortOrder: 3,
    },
    {
      key: 'contactPhone',
      type: 'phone',
      label: 'Phone Number',
      placeholder: '+1 (555) 000-0000',
      source: 'profile',
      validation: { required: false },
      width: 'half',
      sortOrder: 4,
    },
    {
      key: 'birthdate',
      type: 'date',
      label: 'Date of Birth',
      source: 'profile',
      validation: { required: true },
      width: 'half',
      sortOrder: 5,
    },
    {
      key: 'mediaSection',
      type: 'heading',
      label: 'Media',
      sortOrder: 6,
    },
    {
      key: 'headshot',
      type: 'file',
      label: 'Photo / Headshot',
      description: 'Recent photo or headshot (JPG or PNG, max 5MB)',
      validation: {
        required: true,
        fileTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5 * 1024 * 1024,
      },
      width: 'full',
      sortOrder: 7,
    },
    {
      key: 'videoLink',
      type: 'video',
      label: 'Audition Video',
      description: 'Link to your audition video (YouTube, Vimeo, etc.)',
      validation: { required: false },
      width: 'full',
      sortOrder: 8,
    },
  ],
};

/**
 * Dance company specific application structure.
 * Used when the auditing organization is a company.
 */
export const DANCE_COMPANY_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    ...DEFAULT_APPLICATION_STRUCTURE.fields,
    {
      key: 'experienceSection',
      type: 'heading',
      label: 'Experience',
      sortOrder: 9,
    },
    {
      key: 'height',
      type: 'number',
      label: 'Height (cm)',
      source: 'profile',
      validation: { required: true, min: 100, max: 250 },
      width: 'half',
      sortOrder: 10,
    },
    {
      key: 'nationalities',
      type: 'multiselect',
      label: 'Nationalities',
      source: 'profile',
      validation: { required: true },
      width: 'half',
      sortOrder: 11,
    },
    {
      key: 'bio',
      type: 'textarea',
      label: 'Bio / Cover Letter',
      description: 'Tell us about yourself and your dance experience',
      placeholder:
        'Describe your training, experience, and why you want to join...',
      source: 'profile',
      validation: { required: false, maxLength: 2000 },
      width: 'full',
      sortOrder: 12,
    },
  ],
};

/**
 * Workshop registration structure.
 */
export const WORKSHOP_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    {
      key: 'personalSection',
      type: 'heading',
      label: 'Personal Information',
      sortOrder: 0,
    },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 1,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Contact Email',
      source: 'profile',
      validation: { required: true },
      width: 'full',
      sortOrder: 3,
    },
    {
      key: 'contactPhone',
      type: 'phone',
      label: 'Phone Number',
      source: 'profile',
      validation: { required: true },
      width: 'half',
      sortOrder: 4,
    },
    {
      key: 'experienceLevel',
      type: 'select',
      label: 'Experience Level',
      options: [
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'professional', label: 'Professional' },
      ],
      validation: { required: true },
      width: 'half',
      sortOrder: 5,
    },
  ],
};

/**
 * Networking event registration structure.
 */
export const NETWORKING_EVENT_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    {
      key: 'personalSection',
      type: 'heading',
      label: 'Personal Information',
      sortOrder: 0,
    },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 1,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Contact Email',
      source: 'profile',
      validation: { required: true },
      width: 'full',
      sortOrder: 3,
    },
    {
      key: 'bio',
      type: 'textarea',
      label: 'Bio',
      description: 'Tell us about yourself',
      source: 'profile',
      validation: { required: false, maxLength: 1000 },
      width: 'full',
      sortOrder: 4,
    },
    {
      key: 'headshot',
      type: 'file',
      label: 'Photo',
      validation: {
        required: true,
        fileTypes: ['image/jpeg', 'image/png'],
        maxFileSize: 5 * 1024 * 1024,
      },
      width: 'full',
      sortOrder: 5,
    },
  ],
};

/**
 * Video audition structure.
 */
export const VIDEO_AUDITION_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    {
      key: 'personalSection',
      type: 'heading',
      label: 'Personal Information',
      sortOrder: 0,
    },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 1,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Contact Email',
      source: 'profile',
      validation: { required: true },
      width: 'full',
      sortOrder: 3,
    },
    {
      key: 'mediaSection',
      type: 'heading',
      label: 'Video Submission',
      sortOrder: 4,
    },
    {
      key: 'videoLink',
      type: 'video',
      label: 'Audition Video',
      description: 'Link to your audition video (YouTube, Vimeo, etc.)',
      validation: { required: true },
      width: 'full',
      sortOrder: 5,
    },
    {
      key: 'videoDescription',
      type: 'textarea',
      label: 'Video Description',
      description: 'Describe your video and what styles are shown',
      validation: { required: false, maxLength: 500 },
      width: 'full',
      sortOrder: 6,
    },
  ],
};

/**
 * Job casting for a client show (Fever). Mirrors the public apply form of
 * The Ballet Agency (name, stage name, email, city, headshot, up to 5 dance
 * images, resume, reel URL, website, Instagram) and adds a WhatsApp number and
 * a required consent so a candidate we keep for future castings is on record.
 * Identity, city, stage name, website and Instagram backfill the profile
 * (fill-if-null); media and the reel URL stay on the application.
 */
export const FEVER_CASTING_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    {
      key: 'personalSection',
      type: 'heading',
      label: 'About you',
      sortOrder: 0,
    },
    {
      key: 'ageNotice',
      type: 'paragraph',
      label: 'This casting is for dancers aged 18 and over.',
      sortOrder: 1,
    },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      source: 'profile',
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 3,
    },
    {
      key: 'stageName',
      type: 'text',
      label: 'Preferred Name / Stage Name',
      source: 'profile',
      validation: { required: false, maxLength: 255 },
      width: 'full',
      sortOrder: 4,
    },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Email',
      source: 'profile',
      validation: { required: true },
      width: 'half',
      sortOrder: 5,
    },
    {
      key: 'contactPhone',
      type: 'phone',
      label: 'WhatsApp number',
      description: 'With country code, so we can reach you quickly.',
      source: 'profile',
      validation: { required: false },
      width: 'half',
      sortOrder: 6,
    },
    {
      key: 'city',
      type: 'text',
      label: 'Where are you based? (City, Country)',
      source: 'profile',
      validation: { required: false, maxLength: 255 },
      width: 'full',
      sortOrder: 7,
    },
    {
      key: 'mediaSection',
      type: 'heading',
      label: 'Photos and reel',
      sortOrder: 8,
    },
    {
      key: 'headshot',
      type: 'file',
      label: 'Headshot',
      validation: {
        required: true,
        fileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
        maxFileSize: 10 * 1024 * 1024,
      },
      width: 'full',
      sortOrder: 9,
    },
    {
      key: 'danceImages',
      type: 'file',
      label: 'Dance Images',
      description: 'Up to 5 images.',
      allowMultiple: true,
      maxFiles: 5,
      validation: {
        required: true,
        fileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
        maxFileSize: 10 * 1024 * 1024,
      },
      width: 'full',
      sortOrder: 10,
    },
    {
      key: 'resume',
      type: 'file',
      label: 'Dance Resume',
      description: 'PDF only.',
      validation: {
        required: false,
        fileTypes: ['application/pdf'],
        maxFileSize: 10 * 1024 * 1024,
      },
      width: 'full',
      sortOrder: 11,
    },
    {
      key: 'reelUrl',
      type: 'url',
      label: 'Dance Reel URL',
      description:
        'Check that your sharing permissions allow our casting team to view the reel.',
      validation: { required: true, maxLength: 1000 },
      width: 'full',
      sortOrder: 12,
    },
    {
      key: 'linksSection',
      type: 'heading',
      label: 'Links',
      sortOrder: 13,
    },
    {
      key: 'website',
      type: 'url',
      label: 'Website or Portfolio',
      source: 'profile',
      validation: { required: false, maxLength: 500 },
      width: 'half',
      sortOrder: 14,
    },
    {
      key: 'instagramUsername',
      type: 'text',
      label: 'Instagram @',
      source: 'profile',
      validation: { required: false, maxLength: 100 },
      width: 'half',
      sortOrder: 15,
    },
    {
      key: 'consentSection',
      type: 'heading',
      label: 'Consent',
      sortOrder: 16,
    },
    {
      key: 'dataConsent',
      type: 'checkbox',
      label: 'I agree to the storage and use of my photos and data',
      description:
        'Your photos and data are stored for up to 3 years, shared with the client of this casting, and used to contact you about future castings.',
      validation: { required: true },
      width: 'full',
      sortOrder: 17,
    },
  ],
};

/**
 * Blank template — starts with no fields.
 */
export const BLANK_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [],
};

/**
 * Returns the default application structure for an audition.
 *
 * Competition-type auditions resolve the canonical structure-driven
 * competition form (Profile · Contact · Experience · CV · Photos · Videos,
 * with the enforced/locked booklet fields baked in); everything else resolves
 * the general-audition DEFAULT. This keeps the form-builder fallback
 * consistent with the dancer-facing apply page, which resolves
 * `application_structure ?? getCompetitionApplicationStructure()` for
 * competitions. Each call returns an independent copy.
 */
export function getDefaultApplicationStructure(context?: {
  isCompetition?: boolean;
}): ApplicationStructure {
  if (context?.isCompetition) {
    return getCompetitionApplicationStructure();
  }
  return structuredClone(DEFAULT_APPLICATION_STRUCTURE);
}

/**
 * Returns all available templates as a map.
 * Covers all ApplicationTemplateId values.
 */
export const APPLICATION_FORM_TEMPLATES: Record<string, ApplicationStructure> =
  {
    basic: DEFAULT_APPLICATION_STRUCTURE,
    dance_company: DANCE_COMPANY_APPLICATION_STRUCTURE,
    workshop: WORKSHOP_APPLICATION_STRUCTURE,
    networking_event: NETWORKING_EVENT_APPLICATION_STRUCTURE,
    video_audition: VIDEO_AUDITION_APPLICATION_STRUCTURE,
    fever_casting: FEVER_CASTING_APPLICATION_STRUCTURE,
    blank: BLANK_APPLICATION_STRUCTURE,
  };

/**
 * Display metadata for the template picker — the SINGLE source of the picker's
 * id/name/description. Field structure (and the picker's field count) comes from
 * {@link APPLICATION_FORM_TEMPLATES}, which is what apply actually writes; keeping
 * both in one module prevents the picker previewing a structure it never writes.
 * Order here is the picker's display order.
 */
export const APPLICATION_TEMPLATE_META: ReadonlyArray<{
  id: ApplicationTemplateId;
  name: string;
  description: string;
}> = [
  {
    id: 'blank',
    name: 'Blank Template',
    description: 'Start from scratch with an empty form',
  },
  {
    id: 'basic',
    name: 'Basic Application',
    description: 'Simple form with contact information and dance background',
  },
  {
    id: 'dance_company',
    name: 'Dance Company Application',
    description:
      'Comprehensive form for professional company auditions with media requirements',
  },
  {
    id: 'workshop',
    name: 'Workshop Registration',
    description: 'Simple registration form for workshops and classes',
  },
  {
    id: 'video_audition',
    name: 'Video Audition',
    description: 'Focused on video submission for remote auditions',
  },
  {
    id: 'fever_casting',
    name: 'Job Casting (Fever)',
    description:
      'Public job casting: headshot, dance images, reel, resume and consent',
  },
  {
    id: 'networking_event',
    name: 'Networking Event',
    description:
      'For dance networking events where participants select preferred organizations',
  },
];
